import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphRelatedCatalogs as ParagraphRelatedCatalogsData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphRelatedCatalogs.module.css";

/**
 * Layout 68 - `PARAGRAPH_RELATED_CATALOGS_LAYOUT`: sekcja "Inne rozwiazania".
 *
 * Struktura:
 *   - Header (eyebrow + H2 + linia divider) - inline flex
 *   - Grid 3 kart z dziecmi wybranego katalogu-rodzica (backend fetchuje)
 *   - Karta: Nucleo icon (mint box) + tytul + subtitle + CTA link
 *
 * Style w local module.css - port 1:1 z okna WRelatedCatalogs.
 */
export function ParagraphRelatedCatalogs({
  paragraph,
  t,
}: {
  paragraph: ParagraphRelatedCatalogsData;
  t: ParagraphTranslations;
}) {
  if (!Array.isArray(paragraph.items) || paragraph.items.length === 0) return null;

  const eyebrow = (paragraph.text_1 ?? "").trim();
  const heading = paragraph.name;
  const ctaLabel = paragraph.cta_label ?? t.relatedCatalogs.readMore;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
        <span className={styles.line} aria-hidden="true" />
      </div>

      <ul className={styles.grid}>
        {paragraph.items.map((item) => {
          const href = extractInternalHref(item.href) ?? item.href;
          return (
            <li key={item.id} className={styles.card}>
              <Link href={href} className={styles.cardLink} aria-label={item.name}>
                {item.icon ? (
                  <span className={styles.iconBox} aria-hidden="true">
                    <i className={`nc-icon-outline ${item.icon}`} />
                  </span>
                ) : (
                  <span className={styles.iconBoxEmpty} aria-hidden="true" />
                )}

                <h3 className={styles.cardName}>{item.name}</h3>
                {item.subtitle ? <p className={styles.cardSubtitle}>{item.subtitle}</p> : null}

                <span className={styles.cta}>{ctaLabel}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
